import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconRoute } from '@tabler/icons-react';
import { CrudMode, CrudSchemaField, FormItemValueType, SelectOptions, type EnsoFormItem } from '@ohpen/enso-ui';
import { Button, Form } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

import { TabProps } from '../interfaces';
import { FieldType } from '@pmp/api';
import { OPERATION_ORDER, operationToFieldTypeMap } from '../utils';

export const Payments: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const watchedOperations = Form.useWatch(['registrationDetails', 'operations'], form);
  const watchedPaymentOps = Form.useWatch(['payments', 'operations'], form);
  const seededRef = useRef(false);

  const operations: string[] = useMemo(() => {
    if (!watchedOperations) return [];
    return [...watchedOperations].sort((a, b) => OPERATION_ORDER.indexOf(a) - OPERATION_ORDER.indexOf(b));
  }, [watchedOperations]);

  const [bankDetailsVisible, setBankDetailsVisible] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (seededRef.current || !watchedPaymentOps) return;
    seededRef.current = true;
    const visible = new Set<string>();
    for (const op of Object.keys(watchedPaymentOps)) {
      const data = watchedPaymentOps[op];
      if (data?.accountName || data?.sortCode || data?.accountNumber || data?.bankName) {
        visible.add(op);
      }
    }
    if (visible.size > 0) {
      setBankDetailsVisible(visible);
    }
  }, [watchedPaymentOps]);

  const addBankDetails = useCallback((operation: string) => {
    setBankDetailsVisible(prev => new Set(prev).add(operation));
  }, []);

  const removeBankDetails = useCallback((operation: string) => {
    setBankDetailsVisible(prev => {
      const next = new Set(prev);
      next.delete(operation);
      return next;
    });
    form?.setFieldValue(['payments', 'operations', operation], undefined);
  }, [form]);

  const paymentRouteOptions: SelectOptions = useMemo(() => {
    if (!operations?.length) return [];

    return operations.flatMap(operation => {
      const fieldType = operationToFieldTypeMap[operation];
      if (!fieldType) return [];

      return selectsOptions[fieldType] ?? [];
    });
  }, [operations, selectsOptions]);

  const bankDetailFields = useCallback(
    (operation: string): EnsoFormItem[] => [
      {
        title: t('lender.firms.labels.accountName'),
        dataIndex: ['payments', 'operations', operation, 'accountName'],
        valueType: FormItemValueType.text,
        readonly,
        formItemProps: { rules: [{ required: true }] },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.sortCode'),
        dataIndex: ['payments', 'operations', operation, 'sortCode'],
        valueType: FormItemValueType.text,
        readonly,
        formItemProps: { rules: [{ required: true }] },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.accountNumber'),
        dataIndex: ['payments', 'operations', operation, 'accountNumber'],
        valueType: FormItemValueType.text,
        readonly,
        formItemProps: { rules: [{ required: true }] },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.bankName'),
        dataIndex: ['payments', 'operations', operation, 'bankName'],
        valueType: FormItemValueType.text,
        readonly,
        colProps: { span: 6 },
      },
    ],
    [readonly, t],
  );

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.firms.labels.defaultPaymentRoute'),
            dataIndex: ['payments', 'defaultPaymentRouteFirmId'],
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              optionFilterProp: 'label',
              showSearch: true,
              options: paymentRouteOptions,
            },
            formItemProps: {
              rules: [{ required: true }],
              tooltip: t('lender.firms.labels.paymentRouteTooltip'),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.otherPaymentRoutes'),
            dataIndex: ['payments', 'paymentRouteFirmIds'],
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              mode: 'multiple',
              options: paymentRouteOptions,
            },
            formItemProps: {
              tooltip: t('lender.firms.labels.paymentRouteTooltip'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      ...operations
        .filter(operation => !readonly || bankDetailsVisible.has(operation))
        .map(operation => {
          const hasBank = bankDetailsVisible.has(operation);
          const operationLabel = selectsOptions[FieldType.OPERATION_TYPE]?.find(opt => opt.value === operation)?.label;

          return {
            title: `${t('lender.firms.labels.accountFor')} ${operationLabel ?? operation}`,
            valueType: FormItemValueType.group,
            readonly,
            gutter: [24, 0],
            columns: hasBank
              ? [
                ...bankDetailFields(operation),
                ...(!readonly
                  ? [
                    {
                      renderFormItem: () => (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => removeBankDetails(operation)}
                        >
                          {t('global.buttons.delete')}
                        </Button>
                      ),
                      colProps: { span: 24 },
                    },
                  ]
                  : []),
              ]
              : [
                {
                  renderFormItem: () => (
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => addBankDetails(operation)}
                    >
                      {t('global.buttons.add')}
                    </Button>
                  ),
                  colProps: { span: 24 },
                },
              ],
          };
        }),
    ],
    [selectsOptions, operations, readonly, t, paymentRouteOptions, bankDetailsVisible, bankDetailFields, addBankDetails, removeBankDetails],
  );

  const schema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconRoute />,
      title: t('lender.firms.labels.payments'),
      columns,
    }),
    [columns, t],
  );

  return <CrudSchemaField item={schema} form={form} readonly={readonly} />;
};
